/* eslint-disable */

export default function (Vue, config) {
  Object.defineProperty(Vue.prototype, '$gapi', { value: new Gapi(config) })
}

class Gapi {
  /**
   * Config is an object containing these fields:
   *   apiKey: String
   *   clientId: String
   *   scope: String
   *   discoveryDocs: [String]
   *
   * For more information, see  https://developers.google.com/api-client-library/javascript/reference/referencedocs#gapiclientinitargs
   */
  constructor (config) {
    this._config = config

    this._authStateChangeCallbacks = []

    this._clientResolveFn = null
    this._client = new Promise((resolve, reject) => {
      this._clientResolveFn = resolve
    })

    this._gsiClient = null

    this._initialize()
  }

  async _initialize () {
    this._googleAccounts = await this._loadGsiScript()
    await this._loadGsi(this._googleAccounts)

    this._gapi = await this._loadGapiScript()
    await this._loadGapi(this._gapi)
    this._clientResolveFn(this._gapi.client)
  }

  /**
   * Adds the gapi script to the document header, and returns the gapi object.
   *
   * Note: Only call this once.
   */
  _loadGapiScript () {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = 'https://apis.google.com/js/api.js'
      let limit = setTimeout(() => {
        script.remove()
        reject(new Error('Timeout while loading gapi'))
      }, 5000)

      script.onload = () => {
        clearTimeout(limit)
        // The object should have loaded successfully. If it didn't, fail.
        if (!window.gapi) {
          reject(new Error('Error while loading gapi'))
        }
        resolve(window.gapi)
      }

      document.head.appendChild(script)
    })
  }

  /**
   * Performs the necessary initialization of the gapi object. Once that is
   * done, it stores the necessary objects in this._client (which are promises)
   * for easy access later on.
   */
  _loadGapi (gapi) {
    return new Promise((resolve, reject) => {
      gapi.load('client', {
        timeout: 5000,
        callback: () => {
          gapi.client.init({
            apiKey: this._config.apiKey,
            discoveryDocs: this._config.discoveryDocs
          }).then(() => {
            resolve()
          })
        },
        onerror: err => {
          reject(new Error(`Error on gapi load: ${err}`))
        },
        ontimeout: () => {
          reject(new Error(`Timeout on gapi load`))
        }
      })
    })
  }

  /**
   * Adds the gsi script to the document header, and returns the gsi object.
   *
   * Note: Only call this once.
   */
  _loadGsiScript () {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      let limit = setTimeout(() => {
        script.remove()
        reject(new Error('Timeout while loading gsi client'))
      }, 5000)

      script.onload = () => {
        clearTimeout(limit)
        // The object should have loaded successfully. If it didn't, fail.
        if (!window.google.accounts) {
          reject(new Error('Error while loading gsi client'))
        }
        resolve(window.google.accounts)
      }

      document.head.appendChild(script)
    })
  }

  /**
   * Performs the necessary initialization of the gsi object. Once that is
   * done, it stores the necessary objects in this._gsiClient (which is a
   * promise) for easy access later on.
   */
  _loadGsi (googleAccounts) {
    this._gsiClient = googleAccounts.oauth2.initCodeClient({
      client_id: this._config.clientId,
      scope: this._config.scope,
      ux_mode: 'popup',
      callback: async (response) => {
        try {
          const authResponse = await fetch(this._config.accessTokenEndpoint, {
            method: 'POST',
            mode: 'cors',
            cache: 'no-cache',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-By': 'TasksPlus'
            },
            body: JSON.stringify(response)
          })
          if (!authResponse.ok) {
            throw new Error('Got non-2xx response')
          }
          const data = await authResponse.json()
          this.addAccountToLocalStorage(
            data.access_token,
            data.refresh_token)
        } catch (err) {
          // This on catches network failures, not non-2xx HTTP errors.
          console.error(err)
        }
      }
    })
  }

  addAccount () {
    this._gsiClient.requestCode()
  }

  async refreshAccount () {
    const refreshToken = localStorage.getItem('auth:refreshToken')
    if (!refreshToken) {
      return Promise.resolve()
    }
    const postBody = { 'refreshToken': refreshToken }
    try {
      const authResponse = await fetch(this._config.refreshTokenEndpoint, {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        headers:
            { 'Content-Type': 'application/json', 'X-Requested-By': 'TasksPlus' },
        body: JSON.stringify(postBody)
      })
      if (!authResponse.ok) {
        throw new Error('Got non-2xx response')
      }
      const data = await authResponse.json()
      this.updateAccountInLocalStorage(data.access_token)
    } catch (err) {
      // This on catches network failures, not non-2xx HTTP errors.
      console.error(err)
    }
  }

  addAccountToLocalStorage (accessToken, refreshToken) {
    if (accessToken !== undefined && refreshToken !== undefined) {
      localStorage.setItem('auth:accessToken', accessToken)
      localStorage.setItem('auth:refreshToken', refreshToken)
      this._runAuthChangeCallbacks()
    }
  }

  updateAccountInLocalStorage (accessToken) {
    localStorage.setItem('auth:accessToken', accessToken)
    this._runAuthChangeCallbacks()
  }

  // Returns the access token as a string.
  getAccessTokenFromLocalStorage () {
    return localStorage.getItem('auth:accessToken')
  }

  isLoggedIn () {
    return !!localStorage.getItem('auth:accessToken')
  }

  signOut () {
    let accessToken = this.getAccessTokenFromLocalStorage()
    this._googleAccounts.oauth2.revoke(accessToken)
    localStorage.removeItem('auth:accessToken')
    localStorage.removeItem('auth:refreshToken')
    this._runAuthChangeCallbacks()
  }

  _runAuthChangeCallbacks () {
    for (let callback of this._authStateChangeCallbacks) {
      callback()
    }
  }

  addAuthStateChangeCallback (fn) {
    this._authStateChangeCallbacks.push(fn)
  }

  client () {
    return this._client
  }
}
